import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment-timezone';
import { filter, finalize, merge } from 'rxjs';
import { SharedDialogComponent } from 'src/app/shared/components/shared-dialog/shared-dialog.component';
import { NotificationBanner } from 'src/app/shared/models/notification-banner.model';
import { bannerErrorKey, NotificationBannerService } from 'src/app/shared/services/notification-banner.service';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';

type Schedule = 'start' | 'end';
interface ResolvedTime {
  iso?: string;
  error?: 'nonexistent' | 'ambiguous';
}

const validTime = (control: AbstractControl): ValidationErrors | null =>
  !control.value || moment(control.value, 'hh:mm A', true).isValid() ? null : { time: true };

@Component({
  selector: 'app-notification-banner-editor',
  standalone: false,
  templateUrl: './notification-banner-editor.component.html',
  styleUrls: ['./notification-banner-editor.component.scss']
})
export class NotificationBannerEditorComponent implements OnInit {
  public readonly translationService = inject(TranslationService);
  private readonly bannerService = inject(NotificationBannerService);
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<NotificationBannerEditorComponent, boolean>);
  private readonly destroyRef = inject(DestroyRef);
  public readonly data: { id?: string } = inject(MAT_DIALOG_DATA);

  public readonly schedules: Schedule[] = ['start', 'end'];
  public readonly zone = moment.tz.guess();
  public readonly timezoneHelpText = this.translationService
    .translate('bannerTimezoneHelpText')
    .replace('{timezone}', `${this.zone}: UTC${moment.tz(this.zone).format('Z')}`);
  public loading = false;
  public saving = false;
  public errorKey = '';
  private original?: NotificationBanner;

  private readonly fb = inject(FormBuilder);
  public readonly form = this.fb.group(
    {
      title: this.fb.nonNullable.control('', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(200)]),
      message: this.fb.nonNullable.control('', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(500)]),
      startDate: this.fb.control<Date | null>(null, Validators.required),
      startTime: this.fb.control<string | null>('12:00 AM', [Validators.required, validTime]),
      endDate: this.fb.control<Date | null>(null, Validators.required),
      endTime: this.fb.control<string | null>('12:00 AM', [Validators.required, validTime]),
      isActive: this.fb.nonNullable.control(false),
      isTemplate: this.fb.nonNullable.control(false)
    },
    { validators: (group) => this.validateSchedule(group) }
  );

  ngOnInit(): void {
    merge(this.dialogRef.backdropClick(), this.dialogRef.keydownEvents().pipe(filter((event) => event.key === 'Escape')))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.requestClose());
    if (this.data?.id) {
      this.loadBanner();
    }
  }

  get ready(): boolean {
    return !this.data?.id || !!this.original;
  }

  loadBanner(): void {
    this.loading = true;
    this.errorKey = '';
    this.form.disable();
    this.bannerService
      .getBanner(this.data.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (banner) => {
          this.original = banner;
          const start = moment.tz(banner.startAt, this.zone);
          const end = moment.tz(banner.endAt, this.zone);
          this.form.reset({
            title: banner.title,
            message: banner.message,
            startDate: start.toDate(),
            startTime: start.format('hh:mm A'),
            endDate: end.toDate(),
            endTime: end.format('hh:mm A'),
            isActive: banner.isActive,
            isTemplate: banner.isTemplate
          });
          this.form.enable();
        },
        error: (error: unknown) => (this.errorKey = bannerErrorKey(error, 'bannerLoadErrorText'))
      });
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.ready || this.saving) {
      return;
    }
    const { title, message, isActive, isTemplate } = this.form.getRawValue();
    const request = {
      title,
      message,
      isActive,
      isTemplate,
      startAt: this.resolve(this.form, 'start').iso,
      endAt: this.resolve(this.form, 'end').iso
    };
    this.saving = true;
    this.errorKey = '';
    this.form.disable();
    this.bannerService
      .saveBanner(this.data?.id, request)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.saving = false;
          this.form.enable();
        })
      )
      .subscribe({
        next: (saved) => saved && this.dialogRef.close(true),
        error: (error: unknown) => (this.errorKey = bannerErrorKey(error, 'bannerSaveErrorText'))
      });
  }

  requestClose(): void {
    if (this.saving) {
      return;
    }
    if (!this.form.dirty) {
      this.dialogRef.close(false);
      return;
    }
    const translate = (key: string) => this.translationService.translate(key);
    this.dialog
      .open<SharedDialogComponent, SharedDialogComponent['data'], boolean>(SharedDialogComponent, {
        data: {
          title: translate('bannerDiscardTitleText'),
          message: translate('bannerDiscardConfirmText'),
          buttonText: translate('yesText'),
          cancelButtonText: translate('noText')
        }
      })
      .afterClosed()
      .pipe(filter((discard) => discard === true))
      .subscribe(() => this.dialogRef.close(false));
  }

  timeError(which: Schedule): string {
    return this.form.getError(which) ?? '';
  }

  timeControl(which: Schedule): FormControl<string | null> {
    return which === 'start' ? this.form.controls.startTime : this.form.controls.endTime;
  }

  utcReference(which: Schedule): string {
    const { iso } = this.resolve(this.form, which);
    if (!iso) {
      return '';
    }
    return `UTC: ${this.bannerService.formatDateTime(iso, true)}`;
  }

  private validateSchedule(group: AbstractControl): ValidationErrors | null {
    const start = this.resolve(group, 'start');
    const end = this.resolve(group, 'end');
    const errors: ValidationErrors = {
      ...(start.error ? { start: start.error } : {}),
      ...(end.error ? { end: end.error } : {}),
      ...(start.iso && end.iso && Date.parse(end.iso) <= Date.parse(start.iso) ? { endAfterStart: true } : {})
    };
    return Object.keys(errors).length ? errors : null;
  }

  /** Untouched loaded times are sent back exactly; edited ones are minute-precision wall times in the admin's timezone. */
  private resolve(group: AbstractControl, which: Schedule): ResolvedTime {
    const dateControl = group.get(`${which}Date`);
    const timeControl = group.get(`${which}Time`);
    const date: Date | null = dateControl?.value;
    const time: string | null = timeControl?.value;
    if (!date || !time) {
      return {};
    }
    if (this.original && dateControl.pristine && timeControl.pristine) {
      return { iso: this.original[`${which}At`] };
    }
    const parsedTime = moment(time, 'hh:mm A', true);
    if (!parsedTime.isValid()) {
      return {};
    }
    const hour = parsedTime.hour();
    const minute = parsedTime.minute();
    const at = moment.tz({ year: date.getFullYear(), month: date.getMonth(), date: date.getDate(), hour, minute }, this.zone);
    if (at.hours() !== hour || at.minutes() !== minute) {
      return { error: 'nonexistent' };
    }
    const wallTime = at.format('YYYY-MM-DD HH:mm');
    if ([-1, 1].some((hours) => at.clone().add(hours, 'hours').format('YYYY-MM-DD HH:mm') === wallTime)) {
      return { error: 'ambiguous' };
    }
    return { iso: at.utc().format() };
  }
}
